import type { LucideIcon } from "lucide-react";
import { formatNumber } from "./format";

export type PreviewSettings = {
  size: number;
  strokeWidth: number;
  color: string;
  padding: number;
  backgroundMode: "transparent" | "solid";
  backgroundColor: string;
  zoom: number;
  rotation: number;
};

type IconPreviewProps = {
  Icon: LucideIcon;
  settings: PreviewSettings;
  svgRef: React.RefObject<SVGSVGElement>;
  title: string;
  zoomLabel: string;
  rotationLabel: string;
  outputLabel: string;
  onZoomChange: (value: number) => void;
  onRotationChange: (value: number) => void;
};

export function IconPreview({
  Icon,
  settings,
  svgRef,
  title,
  zoomLabel,
  rotationLabel,
  outputLabel,
  onZoomChange,
  onRotationChange,
}: IconPreviewProps) {
  const totalSize = settings.size + settings.padding * 2;
  const isTransparent = settings.backgroundMode === "transparent";
  const scaledSize = totalSize * settings.zoom;

  return (
    <section className="flc-panel flc-preview">
      <div className="flc-panel-header">
        <h2>{title}</h2>
        <div className="flc-preview-controls">
          <div className="flc-zoom">
            <label htmlFor="flc-zoom" className="flc-label">
              {zoomLabel}
            </label>
            <input
              id="flc-zoom"
              type="range"
              min={0.5}
              max={4}
              step={0.1}
              value={settings.zoom}
              onChange={(event) => onZoomChange(Number(event.target.value))}
            />
            <span className="flc-metric">{formatNumber(settings.zoom)}x</span>
          </div>
          <div className="flc-zoom">
            <label htmlFor="flc-rotation" className="flc-label">
              {rotationLabel}
            </label>
            <input
              id="flc-rotation"
              type="range"
              min={0}
              max={360}
              step={1}
              value={settings.rotation}
              onChange={(event) => onRotationChange(Number(event.target.value))}
            />
            <span className="flc-metric">{settings.rotation}°</span>
          </div>
        </div>
      </div>

      <div className="flc-preview-stage">
        <div
          className="flc-preview-viewport"
          style={{
            width: scaledSize,
            height: scaledSize,
          }}
        >
          <div
            className={`flc-preview-canvas ${isTransparent ? "transparent" : ""}`}
            style={{
              width: totalSize,
              height: totalSize,
              padding: settings.padding,
              backgroundColor: isTransparent
                ? undefined
                : settings.backgroundColor,
              transform: `scale(${settings.zoom})`,
            }}
          >
            <Icon
              ref={svgRef}
              size={settings.size}
              strokeWidth={settings.strokeWidth}
              color={settings.color}
              style={{
                transform: `rotate(${settings.rotation}deg)`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="flc-preview-meta">
        <span>{outputLabel}</span>
        <strong>{totalSize}px</strong>
      </div>
    </section>
  );
}
